import { FormEvent, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../../../../shared/components/Button";
import UserAvatar from "../../../../shared/components/UserAvatar";
import useBodyScrollLock from "../../../../shared/hooks/useBodyScrollLock";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { listPublicPlayers } from "../../../players/shared/services/publicPlayerProfileService";
import type { PublicPlayerListItem } from "../../../players/shared/types/publicPlayerProfile";
import type {
  OpenMatchInitialParticipant,
  OpenMatchItem,
  OpenMatchParticipantType,
} from "../../shared/types/matches";

type MatchParticipantModalProps = {
  open: boolean;
  match: OpenMatchItem | null;
  saving: boolean;
  onSubmit: (
    match: OpenMatchItem,
    payload: OpenMatchInitialParticipant,
  ) => Promise<void>;
  onClose: () => void;
};

export default function MatchParticipantModal({
  open,
  match,
  saving,
  onSubmit,
  onClose,
}: MatchParticipantModalProps) {
  const texts = siteTexts.openMatches;
  const [participantType, setParticipantType] =
    useState<OpenMatchParticipantType>("registered");
  const [participantSearch, setParticipantSearch] = useState("");
  const [participantOptions, setParticipantOptions] = useState<
    PublicPlayerListItem[]
  >([]);
  const [participantUserId, setParticipantUserId] = useState("");
  const [participantName, setParticipantName] = useState("");
  const [participantCpf, setParticipantCpf] = useState("");

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open, saving]);

  useEffect(() => {
    if (!open || participantType !== "registered") return;
    let cancelled = false;
    listPublicPlayers({ search: participantSearch })
      .then((response) => {
        if (!cancelled) setParticipantOptions(response.players || []);
      })
      .catch(() => {
        if (!cancelled) setParticipantOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, participantSearch, participantType]);

  useEffect(() => {
    if (!open) return;
    setParticipantType("registered");
    setParticipantSearch("");
    setParticipantOptions([]);
    setParticipantUserId("");
    setParticipantName("");
    setParticipantCpf("");
  }, [open, match?.id]);

  const unavailableUserIds = useMemo(() => {
    const ids = new Set<number>();
    if (match?.organizer?.id) ids.add(Number(match.organizer.id));
    (match?.participants || []).forEach((participant) => {
      if (
        participant.userId &&
        ["confirmed", "pending", "invited"].includes(participant.status)
      ) {
        ids.add(Number(participant.userId));
      }
    });
    return ids;
  }, [match]);

  const availableParticipantOptions = useMemo(
    () =>
      participantOptions.filter(
        (participant) => !unavailableUserIds.has(Number(participant.id)),
      ),
    [participantOptions, unavailableUserIds],
  );

  const selectedParticipant = useMemo(
    () =>
      availableParticipantOptions.find(
        (participant) => String(participant.id) === participantUserId,
      ) || null,
    [availableParticipantOptions, participantUserId],
  );

  const submitDisabled =
    saving ||
    !match ||
    (participantType === "registered" && !selectedParticipant) ||
    (participantType === "external" && !participantName.trim());

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!match || submitDisabled) return;
    const payload: OpenMatchInitialParticipant =
      participantType === "registered" && selectedParticipant
        ? {
            participantType: "registered",
            userId: selectedParticipant.id,
            participantName: selectedParticipant.displayName,
          }
        : participantType === "external"
          ? {
              participantType: "external",
              participantName: participantName.trim(),
              participantCpf: participantCpf.trim(),
              status: "confirmed",
            }
          : {
              participantType: "placeholder",
              participantName: "",
              participantCpf: "",
              status: "confirmed",
            };
    await onSubmit(match, payload);
  }

  if (!open || !match) return null;

  return createPortal(
    <div
      className="match-participant-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={texts.detail.addParticipant}
    >
      <button
        type="button"
        className="match-participant-modal__scrim"
        aria-label={siteTexts.common.cancel}
        onClick={() => {
          if (!saving) onClose();
        }}
      />
      <form className="match-participant-modal" onSubmit={handleSubmit}>
        <header className="match-participant-modal__header">
          <div>
            <span className="material-symbols-outlined" aria-hidden="true">
              person_add
            </span>
            <div>
              <h3>{texts.detail.addParticipant}</h3>
              <p>{match.clubName}</p>
            </div>
          </div>
          <Button
            type="button"
            variant="pill"
            onClick={onClose}
            disabled={saving}
          >
            {siteTexts.common.cancel}
          </Button>
        </header>

        <div className="match-create-panel__participant-picker match-participant-modal__picker">
          <label className="matches-field">
            <span>{texts.form.participantType}</span>
            <select
              value={participantType}
              onChange={(event) => {
                setParticipantType(
                  event.target.value as OpenMatchParticipantType,
                );
                setParticipantUserId("");
              }}
              disabled={saving}
            >
              <option value="registered">{texts.form.participantRegistered}</option>
              <option value="external">{texts.form.participantExternal}</option>
              <option value="placeholder">
                {texts.form.participantPlaceholder}
              </option>
            </select>
          </label>

          {participantType === "registered" ? (
            <>
              <label className="matches-field">
                <span>{texts.form.participantSearch}</span>
                <input
                  type="search"
                  value={participantSearch}
                  onChange={(event) => {
                    setParticipantSearch(event.target.value);
                    setParticipantUserId("");
                  }}
                  placeholder={texts.form.participantSearchPlaceholder}
                  disabled={saving}
                />
              </label>
              <div className="match-athlete-results" role="listbox">
                {availableParticipantOptions.length === 0 ? (
                  <p>{texts.form.participantNoResults}</p>
                ) : (
                  availableParticipantOptions.map((participant) => {
                    const selected = String(participant.id) === participantUserId;
                    return (
                      <button
                        key={participant.id}
                        type="button"
                        className={`match-athlete-result${
                          selected ? " is-selected" : ""
                        }`}
                        role="option"
                        aria-selected={selected}
                        onClick={() => {
                          setParticipantUserId(String(participant.id));
                          setParticipantSearch(participant.displayName);
                        }}
                        disabled={saving}
                      >
                        <UserAvatar
                          name={participant.displayName}
                          avatarUrl={participant.avatarUrl || undefined}
                          sex={participant.sex || undefined}
                          size={36}
                        />
                        <span>
                          <strong>{participant.displayName}</strong>
                          <small>
                            {participant.categoryLabel ||
                              texts.form.participantRegistered}
                          </small>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          ) : participantType === "external" ? (
            <>
              <label className="matches-field">
                <span>{texts.form.participantName}</span>
                <input
                  type="text"
                  value={participantName}
                  onChange={(event) => setParticipantName(event.target.value)}
                  disabled={saving}
                />
              </label>
              <label className="matches-field">
                <span>{texts.form.participantCpf}</span>
                <input
                  type="text"
                  value={participantCpf}
                  onChange={(event) => setParticipantCpf(event.target.value)}
                  disabled={saving}
                />
              </label>
            </>
          ) : null}
        </div>

        {participantType === "registered" && selectedParticipant ? (
          <div className="match-create-panel__participant-preview">
            <UserAvatar
              name={selectedParticipant.displayName}
              avatarUrl={selectedParticipant.avatarUrl || undefined}
              sex={selectedParticipant.sex || undefined}
              size={44}
            />
            <div>
              <strong>{selectedParticipant.displayName}</strong>
              <span>
                {selectedParticipant.categoryLabel ||
                  texts.form.participantRegistered}
              </span>
            </div>
          </div>
        ) : null}

        <div className="match-participant-modal__actions">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={saving}
          >
            {siteTexts.common.cancel}
          </Button>
          <Button type="submit" disabled={submitDisabled}>
            {saving ? texts.detail.addParticipantLoading : texts.detail.addParticipant}
          </Button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
